import { memo, useCallback } from "react";
import { Button, ButtonKind, ButtonSize, ButtonVariant, Heading, Text } from "@stubhub/broadway-web";
import { useFormattedEventDateTime } from "@stubhub/ensemble-discovery";
import { type EventListGridItem, GridType } from "src/http/types";
import { useContent } from "src/shared/components/content";
import { useCommonEventGridListItem } from "src/website-apps/shared/components/common-event-grid-list-item/use-common-event-grid-list-item";
import { Image } from "src/website-apps/shared/components/image";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { festivalCard, festivalCardDetails, festivalCardImage, festivalCardTitle } from "./festival-carousel.recipe";

export const getFestivalEventTitle = (event: EventListGridItem): string =>
  event.eventMetadata?.concerts?.festivalEventName || event.name;

interface FestivalEventItemProps {
  event: EventListGridItem;
  position: number;
  onItemClick: (itemId: string | number) => void;
}

const FestivalEventItemComponent = ({ event, position, onItemClick }: FestivalEventItemProps) => {
  const handleItemClick = useCallback(
    (id: number) => {
      onItemClick(String(id));
    },
    [onItemClick],
  );

  const { eventUrl, handleOnClick, handleOnInView } = useCommonEventGridListItem({
    event,
    gridType: GridType.FestivalFeaturedEvents,
    index: position,
    onClick: handleItemClick,
    previewSummary: { name: event.name, imageUrl: event.festivalImageUrl ?? null },
  });

  const { formattedDayOfWeekDayMonthYear, formattedTime } = useFormattedEventDateTime(
    event.eventMetadata?.common?.eventStartDateTime,
    event.eventMetadata?.common?.eventEndDateTime ?? undefined,
    event.eventMetadata?.common?.venueTimeZoneOffset ?? undefined,
    undefined,
    event.eventMetadata?.common?.passLength ?? undefined,
  );

  const tbaContent = useContent("cid$#3860643#"); // "TBA"
  const seeTicketsContent = useContent("cid$#3562069#"); // "See tickets"

  const dateTimeText = event.isDateConfirmed
    ? `${formattedDayOfWeekDayMonthYear} • ${event.isTimeConfirmed ? formattedTime : tbaContent}`
    : tbaContent;

  return (
    <>
      <InViewTracker onInView={handleOnInView} />
      <a href={eventUrl ?? "#"} onClick={handleOnClick} className={festivalCard}>
        {event.festivalImageUrl ? (
          <Image alt="" className={festivalCardImage} size={{ width: 260, height: 104 }} src={event.festivalImageUrl} />
        ) : null}
        <div className={festivalCardDetails}>
          <div className={festivalCardTitle}>
            <Heading as="h3" color="text" size="medium" variant="title">
              {getFestivalEventTitle(event)}
            </Heading>
          </div>
          <Text color="text.subtle" size="small" variant="body">
            {dateTimeText}
          </Text>
          <Text color="text.subtle" size="small" variant="body">
            {event.venueName ?? ""}
          </Text>
        </div>
        <Button kind={ButtonKind.Primary} size={ButtonSize.Small} variant={ButtonVariant.Outline}>
          {seeTicketsContent}
        </Button>
      </a>
    </>
  );
};

export const FestivalEventItem = memo(FestivalEventItemComponent);
FestivalEventItem.displayName = "FestivalEventItem";
